import type { TableView } from 'owa-mail-list-store';
import { MailRowDataPropertyGetter } from 'owa-mail-list-store';
import { getItem } from 'owa-local-storage';
import { PRIORITIZE_NUDGE_TIMESTAMP } from '../constants';
import { getMailboxInfo } from 'owa-mail-mailboxinfo';
import { getUserStore } from '../store/store';

/**
 * Gets the row keys to prioritize from the given table view.
 * @param tableView The table view to get the row keys from.
 * @isForNudge If true, use the last time the nudge was dismissed OR Prioritize was used
 */
export function getRowsToPrioritize(tableView: TableView, isForNudge?: boolean): string[] {
    const rowKeys = [...tableView.rowKeys];

    const mailboxInfo = getMailboxInfo(tableView);
    const store = getUserStore(mailboxInfo);

    return rowKeys.filter(rowKey => {
        // Filter out email that has already been prioritized and skipped
        if (store.skippedRowKeys.includes(rowKey)) {
            return false;
        }

        // Filter out email that is read
        const isUnread = MailRowDataPropertyGetter.getUnreadCount(rowKey, tableView) > 0;
        if (isUnread === false) {
            return false;
        }

        // Filter out email that has already been prioritized
        const isPrioritized = MailRowDataPropertyGetter.getCopilotInboxScore(rowKey, tableView) > 0;
        if (isPrioritized) {
            return false;
        }

        // - When prioritizing, filter out email that is older than 24 hours ago
        // - For nudge, filter out email older than last nudge timestamp (dismiss time or last
        // prioritize time)
        const receivedTime = MailRowDataPropertyGetter.getLastDeliveryTimeStamp(rowKey, tableView);
        if (!receivedTime) {
            return false;
        }
        const now = new Date();
        const isMonday = now.getDay() === 1;
        const lookbackDays = isMonday ? 3 : 1;
        let filterTime: Date = new Date(
            now.getFullYear(),
            now.getMonth(),
            now.getDate() - lookbackDays
        );

        if (isForNudge) {
            const nudgeTimestamp = getItem(window, PRIORITIZE_NUDGE_TIMESTAMP);

            if (nudgeTimestamp) {
                const parsedTimestamp = parseInt(nudgeTimestamp, 10);
                if (!isNaN(parsedTimestamp)) {
                    const nudgeTime = new Date(parsedTimestamp);
                    const currentTime = now.getTime();
                    // Validate that the parsed timestamp is reasonable
                    const isValidTimestamp = parsedTimestamp > 0 && parsedTimestamp <= currentTime;
                    // Use nudgeTimestamp only if it's more recent than lookback time and valid
                    if (isValidTimestamp && nudgeTime > filterTime) {
                        filterTime = nudgeTime;
                    }
                }
            }
        }

        return new Date(receivedTime) >= filterTime;
    });
}
